import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary line rendered under the label inside the dropdown (optional). */
  description?: string;
  /** Extra searchable text (optional). The label and description are always searched too. */
  searchText?: string;
  /** Small text shown next to the label in the closed trigger (e.g. a requirement id). */
  sublabel?: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  'data-field-name'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * Combobox built on Radix Popover. Renders options with role="option" so the
 * existing Radix-select test helpers (findByRole('option')) keep working. The
 * trigger carries `data-field-name` so focusFirstInvalidField can target it.
 *
 * Keyboard: ArrowDown/ArrowUp move through the filtered options, Enter selects
 * the highlighted option, Escape closes. The search input is focused on open.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  disabled,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options available',
  'data-field-name': dataFieldName,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const id = React.useId();

  const selected = options.find((o) => o.value === value);
  const normalized = query.trim().toLowerCase();
  const filtered = normalized
    ? options.filter((o) =>
        `${o.label} ${o.description ?? ''} ${o.searchText ?? ''}`.toLowerCase().includes(normalized)
      )
    : options;

  React.useEffect(() => {
    if (open) {
      const idx = filtered.findIndex((o) => o.value === value);
      setActiveIndex(idx >= 0 ? idx : 0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(filtered.length - 1, 0)));
  }, [filtered.length, query]);

  const selectOption = (opt: SearchableSelectOption) => {
    onValueChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length) setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          data-field-name={dataFieldName}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-expanded={open}
          title={selected ? [selected.label, selected.description].filter(Boolean).join(' – ') : undefined}
          className={cn(
            'flex h-10 w-full max-w-full min-w-0 items-center justify-between gap-2 rounded-lg border bg-surface px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 text-primary',
            open && 'border-brand-500 ring-1 ring-brand-500',
            ariaInvalid && 'border-error'
          )}
        >
          <span className={cn('flex min-w-0 flex-1 items-center gap-2', !selected && 'text-muted')}>
            <span className="truncate">{selected ? selected.label : placeholder}</span>
            {selected?.sublabel && (
              <span className="shrink-0 text-xs text-secondary">{selected.sublabel}</span>
            )}
          </span>
          <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] max-w-[min(24rem,calc(100vw-2rem))] rounded-lg border bg-surface p-1 text-primary shadow-md"
        >
          <div className="mb-1 flex items-center gap-2 rounded-md border px-2 py-1.5">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={handleInputKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={`${id}-listbox`}
              aria-activedescendant={filtered[activeIndex] ? `${id}-option-${activeIndex}` : undefined}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted"
            />
          </div>
          <div id={`${id}-listbox`} className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted">{emptyMessage}</p>
            ) : (
              filtered.map((opt, i) => (
                <button
                  key={opt.value}
                  id={`${id}-option-${i}`}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => selectOption(opt)}
                  className={cn(
                    'relative flex w-full cursor-default select-none items-start rounded-md py-2 pl-8 pr-2 text-left text-sm text-primary outline-none transition-colors',
                    activeIndex === i && 'bg-hover',
                    opt.value === value && 'bg-brand-50 text-primary'
                  )}
                >
                  <span className="absolute left-2 mt-1 flex h-3.5 w-3.5 items-center justify-center">
                    {opt.value === value && <Check className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-bold text-sm text-primary">{opt.label}</span>
                    {opt.description && (
                      <span className="block whitespace-pre-line text-xs text-secondary leading-relaxed mt-1">{opt.description}</span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
